import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  FiCheckCircle, 
  FiXCircle, 
  FiCheck, 
  FiAlertCircle, 
  FiUser, 
  FiInfo,
  FiTrash2,
  FiFileText,
  FiBriefcase,
  FiBell,
  FiMail
} from 'react-icons/fi';

const PanelContainer = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 320px;
  max-height: 480px;
  background-color: white;
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  margin: 0;
`;

const HeaderAction = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  
  svg {
    margin-bottom: ${({ theme }) => theme.spacing[2]};
    opacity: 0.5;
  }
`;

const NotificationItem = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  position: relative;
  
  ${({ read, theme }) => !read && `
    background-color: ${theme.colors.backgroundLight};
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background-color: ${theme.colors.primary};
    }
  `}
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundLight};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const NotificationIcon = styled.div`
  margin-right: ${({ theme }) => theme.spacing[3]};
  width: 32px;
  height: 32px;
  background-color: ${({ type, theme }) => {
    switch (type) {
      case 'approval':
        return `${theme.colors.success}15`;
      case 'rejection':
        return `${theme.colors.error}15`;
      case 'assignment':
        return `${theme.colors.info}15`;
      case 'reminder':
        return `${theme.colors.warning}15`;
      case 'timeEntry':
        return `${theme.colors.primary}15`;
      case 'report':
        return `${theme.colors.accent}15`;
      case 'email':
        return `${theme.colors.primary}15`;
      default:
        return `${theme.colors.textSecondary}15`;
    }
  }};
  color: ${({ type, theme }) => {
    switch (type) {
      case 'approval':
        return theme.colors.success;
      case 'rejection':
        return theme.colors.error;
      case 'assignment':
        return theme.colors.info;
      case 'reminder':
        return theme.colors.warning;
      case 'timeEntry':
        return theme.colors.primary;
      case 'report':
        return theme.colors.accent;
      case 'email':
        return theme.colors.primary;
      default:
        return theme.colors.textSecondary;
    }
  }};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const NotificationContent = styled.div`
  flex: 1;
`;

const NotificationTitle = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const NotificationMessage = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const NotificationTime = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textTertiary};
`;

const NotificationActions = styled.div`
  display: flex;
  margin-left: ${({ theme }) => theme.spacing[2]};
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  padding: ${({ theme }) => theme.spacing[1]};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  
  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

// Get notification icon based on type
const getNotificationIcon = (type) => {
  switch (type) {
    case 'approval':
      return <FiCheckCircle />;
    case 'rejection':
      return <FiXCircle />;
    case 'assignment':
      return <FiBriefcase />;
    case 'reminder':
      return <FiAlertCircle />;
    case 'timeEntry':
      return <FiFileText />;
    case 'report':
      return <FiFileText />;
    case 'system':
      return <FiInfo />;
    // "Invitations: 12 of 12 e-mails sent" — the outbox batch completion.
    case 'email':
      return <FiMail />;
    default:
      return <FiBell />;
  }
};

// Format relative time
const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMinutes = Math.floor((now - date) / (1000 * 60));
  
  if (diffInMinutes < 1) {
    return 'Just now';
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} min ago`;
  } else if (diffInMinutes < 1440) {
    return `${Math.floor(diffInMinutes / 60)} hours ago`;
  } else if (diffInMinutes < 10080) {
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  } else {
    return date.toLocaleDateString();
  }
};

// Get the dashboard path for the current user's role
const getDashboardPath = (userType) => {
  switch (userType) {
    case 'admin':
      return '/admin/dashboard';
    case 'employee':
      return '/employee/dashboard';
    case 'customer':
      return '/customer/dashboard';
    case 'support':
      return '/support';
    default:
      return '/';
  }
};

// Get navigation path based on the current user's role and the notification's related entity
const getNavigationPath = (notification, userType) => {
  const { relatedId, relatedType } = notification;

  // Notifications without a related entity go to the role dashboard
  if (!relatedId) {
    return getDashboardPath(userType);
  }

  // Accept both 'timeEntry' and the legacy 'time_entry' alias so older/seeded
  // notifications still route correctly.
  if (relatedType === 'timeEntry' || relatedType === 'time_entry') {
    switch (userType) {
      case 'employee':
        return `/employee/time-entries/${relatedId}`;
      case 'customer':
        return `/customer/approvals/${relatedId}`;
      case 'admin':
        return '/admin/time-reports';
      default:
        return getDashboardPath(userType);
    }
  }

  if (userType === 'admin') {
    switch (relatedType) {
      case 'customer':
        return '/admin/customers';
      case 'user':
        return '/admin/users';
      case 'report':
        return '/admin/time-reports';
      default:
        return getDashboardPath(userType);
    }
  }

  return getDashboardPath(userType);
};

const NotificationPanel = forwardRef(({ 
  notifications, 
  loading, 
  onMarkAsRead, 
  onMarkAllAsRead, 
  onDelete 
}, ref) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.read) {
      onMarkAsRead(notification.id);
    }

    // Navigate if applicable
    const path = getNavigationPath(notification, currentUser?.userType);
    if (path) {
      navigate(path);
    }
  };
  
  return (
    <PanelContainer ref={ref}>
      <PanelHeader>
        <HeaderTitle>Notifications</HeaderTitle>
        {notifications.length > 0 && (
          <HeaderAction onClick={onMarkAllAsRead}>
            Mark all as read
          </HeaderAction>
        )}
      </PanelHeader>
      
      <PanelContent>
        {loading ? (
          <EmptyState>Loading notifications...</EmptyState>
        ) : notifications.length === 0 ? (
          <EmptyState>
            <FiBell size={24} />
            <p>No notifications</p>
          </EmptyState>
        ) : (
          notifications.map(notification => (
            <NotificationItem 
              key={notification.id} 
              read={notification.read}
              onClick={() => handleNotificationClick(notification)}
            >
              <NotificationIcon type={notification.type}>
                {getNotificationIcon(notification.type)}
              </NotificationIcon>
              
              <NotificationContent>
                <NotificationTitle>{notification.title}</NotificationTitle>
                <NotificationMessage>{notification.message}</NotificationMessage>
                <NotificationTime>{formatRelativeTime(notification.createdAt)}</NotificationTime>
              </NotificationContent>
              
              <NotificationActions>
                <ActionButton 
                  title="Delete" 
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(notification.id);
                  }}
                >
                  <FiTrash2 />
                </ActionButton>
              </NotificationActions>
            </NotificationItem>
          ))
        )}
      </PanelContent>
    </PanelContainer>
  );
});

export default NotificationPanel;